import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Shield, Folder } from "lucide-react";
import { SubCategoryService } from "../../services/subCategoryService";
import PrefixValidationInput from "../Common/PrefixValidationInput";

const CreateSubCategoryForm = ({
  open,
  onClose,
  organizationId,
  categories,
  onSubCategoryCreated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    id_prefix: "",
    category_id: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Sub-category name is required");
      return;
    }

    if (!formData.id_prefix.trim()) {
      setError("ID prefix is required");
      return;
    }

    if (!formData.category_id) {
      setError("Parent category is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await SubCategoryService.createSubCategory(
        {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          id_prefix: formData.id_prefix.trim().toUpperCase(),
          category_id: formData.category_id,
          organization_id: organizationId,
        },
        getAccessTokenSilently
      );

      // Reset form
      setFormData({
        name: "",
        description: "",
        id_prefix: "",
        category_id: "",
      });
      onSubCategoryCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        name: "",
        description: "",
        id_prefix: "",
        category_id: "",
      });
      setError(null);
      onClose();
    }
  };

  const selectedCategory = categories.find(
    (cat) => cat.id === formData.category_id
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Sub-Category</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Parent Category Selection */}
          <div className="space-y-2">
            <Label htmlFor="category_id">Parent Category *</Label>
            <Select
              value={formData.category_id}
              onValueChange={(value) => handleInputChange("category_id", value)}
              disabled={submitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select parent category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    <div className="flex items-center gap-2">
                      <Folder className="h-3 w-3 text-blue-600" />
                      <span>{category.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {category.id_prefix}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sub-category Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Sub-Category Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="e.g., Network Security, Data Protection, Access Control"
              disabled={submitting}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="e.g., Controls related to network infrastructure security and monitoring"
              disabled={submitting}
              rows={3}
            />
          </div>

          {/* ID Prefix with Validation */}
          <div className="space-y-2">
            <Label htmlFor="id_prefix">ID Prefix *</Label>
            <PrefixValidationInput
              value={formData.id_prefix}
              onChange={(value) => handleInputChange("id_prefix", value)}
              organizationId={organizationId}
              getAccessTokenSilently={getAccessTokenSilently}
              disabled={submitting}
              placeholder="e.g., NETSEC, DATAPR, ACCESS"
            />
          </div>

          {/* Preview */}
          {formData.name && formData.id_prefix && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Preview:</p>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span className="font-medium">{formData.name}</span>
                    <Badge variant="outline" className="font-mono">
                      {formData.id_prefix}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Controls will have IDs like: {formData.id_prefix}-C001,{" "}
                    {formData.id_prefix}-C002
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting}
              className="flex items-center gap-2"
            >
              {submitting && <Spinner size="sm" />}
              Create Sub-Category
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSubCategoryForm;
